import React, { useEffect, useRef, useState, useCallback } from "react";
import { createFrame002Scene, FRAME_002_DEFAULTS } from "./frame002Scene";
import Frame002Panel from "./Frame002Panel";
import InstrumentOverlay from "../InstrumentOverlay";

/**
 * Frame 002 · Production route.
 *
 * Route: /lab/frame-002
 * Specification: /app/memory/FRAME_002_SPECIFICATION.md
 *
 * Two still objects in black space. One-way camera withdraw, one
 * hold. Nothing else.
 */

const PRESET_KEY = "frame002_presentation_preset_v1";

// The rest distance between Object A and Object B, in world units,
// derived from BASE_POSITIONS in frame002Scene.js. Held here so the
// instrument overlay can display it without reaching into the scene.
const REST_DISTANCE_UNIT = Math.sqrt(1.35 * 1.35 + 0.22 * 0.22 + 1.20 * 1.20);

export default function Frame002() {
  const containerRef = useRef(null);
  const sceneApiRef = useRef(null);

  const [params, setParams] = useState({ ...FRAME_002_DEFAULTS });
  const [uiHidden, setUiHidden] = useState(false);
  const [instrumentVisible, setInstrumentVisible] = useState(false);
  const [progress, setProgress] = useState({ state: "idle", phaseT: 0, phaseDuration: 0, phaseFraction: 0 });
  const [hasPresentationPreset, setHasPresentationPreset] = useState(false);

  // Detect existing preset on mount.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(PRESET_KEY);
      setHasPresentationPreset(!!raw);
    } catch {
      setHasPresentationPreset(false);
    }
  }, []);

  // Mount the scene.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const api = createFrame002Scene({
      container,
      initialParams: params,
      onProgress: (p) => setProgress(p),
    });
    sceneApiRef.current = api;

    return () => {
      api.dispose();
      sceneApiRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push param changes.
  useEffect(() => {
    if (sceneApiRef.current) sceneApiRef.current.setParams(params);
  }, [params]);

  // H toggles UI.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      if (e.key === "h" || e.key === "H") setUiHidden((v) => !v);
      if (e.key === "i" || e.key === "I") setInstrumentVisible((v) => !v);
      if (e.key === " " || e.code === "Space") {
        e.preventDefault();
        if (sceneApiRef.current) sceneApiRef.current.play();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onParamChange = useCallback((key, value) => {
    setParams((prev) => ({ ...prev, [key]: value }));
  }, []);

  const play = useCallback(() => {
    if (sceneApiRef.current) sceneApiRef.current.play();
  }, []);
  const stop = useCallback(() => {
    if (sceneApiRef.current) sceneApiRef.current.stop();
  }, []);
  const jumpToEnd = useCallback(() => {
    if (sceneApiRef.current) sceneApiRef.current.jumpToEnd();
  }, []);
  const takeScreenshot = useCallback(() => {
    if (sceneApiRef.current) {
      sceneApiRef.current.screenshot({
        presetName: hasPresentationPreset ? "presentation" : "unsaved",
      });
    }
  }, [hasPresentationPreset]);

  const savePresentationPreset = useCallback(() => {
    try {
      const payload = {
        params: { ...params },
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(PRESET_KEY, JSON.stringify(payload));
      setHasPresentationPreset(true);
    } catch {
      // ignore
    }
  }, [params]);

  const loadPresentationPreset = useCallback(() => {
    try {
      const raw = localStorage.getItem(PRESET_KEY);
      if (!raw) return;
      const payload = JSON.parse(raw);
      if (payload && payload.params) {
        setParams({ ...FRAME_002_DEFAULTS, ...payload.params });
      }
    } catch {
      // ignore
    }
  }, []);

  return (
    <div
      data-testid="frame002-page"
      style={{
        position: "fixed",
        inset: 0,
        background: "#000",
        overflow: "hidden",
      }}
    >
      <div
        ref={containerRef}
        data-testid="frame002-canvas"
        style={{ position: "absolute", inset: 0 }}
      />

      {uiHidden && (
        <button
          onClick={() => setUiHidden(false)}
          data-testid="btn-show-ui"
          style={{
            position: "fixed",
            top: 16,
            right: 16,
            background: "rgba(0,0,0,0.35)",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "rgba(255,255,255,0.5)",
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
            fontSize: 10,
            padding: "4px 10px",
            borderRadius: 3,
            cursor: "pointer",
            letterSpacing: "0.12em",
            zIndex: 10,
          }}
        >
          show (H)
        </button>
      )}

      {!uiHidden && (
        <Frame002Panel
          params={params}
          onParamChange={onParamChange}
          onPlay={play}
          onStop={stop}
          onJumpToEnd={jumpToEnd}
          onScreenshot={takeScreenshot}
          onSavePresentationPreset={savePresentationPreset}
          onLoadPresentationPreset={loadPresentationPreset}
          onHideUI={() => setUiHidden(true)}
          progress={progress}
          hasPresentationPreset={hasPresentationPreset}
        />
      )}

      <InstrumentOverlay
        visible={instrumentVisible}
        frame="frame-002"
        progress={progress}
        params={params}
        restDistance={REST_DISTANCE_UNIT * (params.objectSeparation ?? 1)}
        hasConvergence={false}
      />
    </div>
  );
}
